import { useCallback } from "react";
import { FieldErrors } from "react-hook-form";

interface ScrollToErrorOptions {
  headerOffset?: number;
  scrollBehavior?: ScrollBehavior;
  focusDelay?: number;
}

export const useScrollToError = (options: ScrollToErrorOptions = {}) => {
  const {
    headerOffset = 300,
    scrollBehavior = "smooth",
    focusDelay = 300,
  } = options;

  const scrollToError = useCallback(
    (errors: FieldErrors<any>) => {
      
      const firstErrorField = Object.keys(errors)[0];
      if (!firstErrorField) return;

      const selectors = [
        `[data-field="${firstErrorField}"]`,
        `[name="${firstErrorField}"]`,
        `input[name="${firstErrorField}"]`,
        `textarea[name="${firstErrorField}"]`,
        `select[name="${firstErrorField}"]`,
        `#${firstErrorField}`,
        `[data-testid="${firstErrorField}"]`,
      ];

      let errorElement: Element | null = null;

      for (const selector of selectors) {
        try {
          errorElement = document.querySelector(selector);
          if (errorElement) {
            break;
          }
        } catch (e) {
          
        }
      }

      
      if (
        !errorElement &&
        ["location", "withCover", "poolMaterial"].includes(firstErrorField)
      ) {
        const placeholderTexts = {
          location: "Pool Location",
          withCover: "Cover",
          poolMaterial: "Material",
        };

        const placeholderText =
          placeholderTexts[firstErrorField as keyof typeof placeholderTexts];
        if (placeholderText) {
          
          const allElements = document.querySelectorAll("span, button, div");
          for (const element of allElements) {
            if (element.textContent?.trim() === placeholderText) {
              
              const clickableElement =
                element.closest('button, [role="button"], [tabindex]') ||
                element.closest('div[role="button"]') ||
                element.parentElement?.closest(
                  'button, [role="button"], [tabindex]'
                );

              if (clickableElement) {
                errorElement = clickableElement;

                break;
              }
            }
          }

          
          if (!errorElement) {
            const allElements = document.querySelectorAll("*");
            for (const element of allElements) {
              if (element.textContent?.includes(placeholderText)) {
                
                const clickableParent = element.closest(
                  'button, [role="button"], [tabindex]'
                );
                if (clickableParent) {
                  errorElement = clickableParent;

                  break;
                }
              }
            }
          }
        }
      }

      if (errorElement) {
        
        const elementPosition = errorElement.getBoundingClientRect().top;
        const offsetPosition =
          elementPosition + window.pageYOffset - headerOffset;

        
        window.scrollTo({
          top: offsetPosition,
          behavior: scrollBehavior,
        });

        
        if (
          errorElement instanceof HTMLElement &&
          typeof errorElement.focus === "function"
        ) {
          setTimeout(() => {
            try {
              if (errorElement instanceof HTMLElement) {
                errorElement.focus();
              }
            } catch (e) {
              console.warn(
                `⚠️ Could not focus on element: ${firstErrorField}`,
                e
              );
            }
          }, focusDelay);
        }
      } else {
        console.warn(
          `⚠️ Could not find error element for field: ${firstErrorField}`
        );
      }
    },
    [headerOffset, scrollBehavior, focusDelay]
  );

  return { scrollToError };
};
